import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import HttpClientXml from "../utils/HttpClientXml";
import HomeService from "../Services/HomeService";
import { useNavigate } from "react-router-dom";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "rgb(131, 144, 155)",
  margin: "0px 0px 10px 0px",
  fontWeight: "bold",
};

function AddAppPoster() {
  const [image, setImage] = useState("");
  const [imageLoading, setImageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasExistingPoster, setHasExistingPoster] = useState(false);
  const [checking, setChecking] = useState(true);
  const navigate = useNavigate();

  // Check if poster already exists
  useEffect(() => {
    checkExistingPoster();
  }, []);

  const checkExistingPoster = async () => {
    setChecking(true);
    try {
      const res = await HomeService.getAppPoster();
      if (res?.data && res.data.length > 0) {
        setHasExistingPoster(true);
        toast.error(
          "A poster already exists. Please edit or delete the existing one."
        );
        setTimeout(() => {
          navigate("/manage-app-poster");
        }, 2000);
      }
    } catch (error) {
      console.error("Error checking existing poster:", error);
    } finally {
      setChecking(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate image file
    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file");
      return;
    }

    setImageLoading(true);

    const data = new FormData();
    data.append("image", file);

    try {
      const res = await HttpClientXml.fileUplode("image-upload", "POST", data);
      if (res?.image) {
        setImage(res.image);
        toast.success("Poster image uploaded successfully!");
      } else {
        toast.error(res?.message || "Failed to upload image");
      }
    } catch (error) {
      toast.error("Failed to upload image.");
      console.error("Error uploading image:", error);
    } finally {
      setImageLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check again if poster exists
    if (hasExistingPoster) {
      toast.error("A poster already exists. Redirecting to manage page...");
      setTimeout(() => {
        navigate("/manage-app-poster");
      }, 1500);
      return;
    }

    // Validation
    if (!image) {
      toast.error("Please upload a poster image");
      return;
    }

    setLoading(true);

    // Prepare data according to Postman body structure
    const posterData = {
      image: image,
    };

    console.log("App Poster Data:", posterData);

    try {
      const res = await HomeService.createAppPoster(posterData);
      if (res?.status) {
        toast.success("App poster added successfully!");
        clearForm();
        // Redirect to manage page after successful creation
        setTimeout(() => {
          navigate("/manage-app-poster");
        }, 1500);
      } else {
        toast.error(res?.message || "Failed to add app poster.");
      }
    } catch (error) {
      toast.error("Failed to add app poster.");
      console.error("Error adding app poster:", error);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setImage("");
  };

  const removeImage = () => {
    setImage("");
    toast.success("Image removed");
  };

  const handleGoToManage = () => {
    navigate("/manage-app-poster");
  };

  if (checking) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Add App Download Poster
            </div>
            <hr />
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Checking for existing poster...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasExistingPoster) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid">
          <div className="mt-5">
            <div style={headLineStyle} className="page-headline">
              Add App Download Poster
            </div>
            <hr />
            <div className="card">
              <div className="card-body text-center py-5">
                <div className="alert alert-warning mb-4">
                  <i className="fa-solid fa-exclamation-triangle fa-2x mb-3"></i>
                  <h4>Poster Already Exists!</h4>
                  <p className="mb-0">
                    An app download poster already exists in the system. You
                    cannot create multiple posters. Please edit or delete the
                    existing one.
                  </p>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleGoToManage}
                  style={{
                    background: "rgb(133, 218, 79)",
                    border: "none",
                    padding: "10px 30px",
                    fontSize: "16px",
                    fontWeight: "500",
                    borderRadius: "13px",
                  }}
                >
                  <i className="fa-solid fa-cog me-2"></i>
                  Go to Manage Poster
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <div style={headLineStyle} className="page-headline">
            Add App Download Poster
          </div>
          <hr />

          <section className="chartBox_area">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Image Upload Section */}
                <div className="col-md-12 mt-3">
                  <label htmlFor="image">
                    Upload App Poster Image
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    id="image"
                    className="form-control"
                    onChange={handleImageUpload}
                    accept="image/*"
                    disabled={imageLoading}
                    required
                  />
                  <div className="form-text">
                    {imageLoading ? (
                      <span className="text-primary">
                        <i className="fa-solid fa-spinner fa-spin me-1"></i>
                        Uploading image...
                      </span>
                    ) : (
                      "Upload a poster image for app download promotion"
                    )}
                  </div>

                  {image && (
                    <div className="mt-3 position-relative">
                      <div className="alert alert-success p-2">
                        <strong>Poster Preview:</strong>
                      </div>
                      <img
                        src={image}
                        alt="App Poster Preview"
                        style={{
                          maxHeight: "300px",
                          maxWidth: "100%",
                          borderRadius: "8px",
                          border: "2px solid #dee2e6",
                          objectFit: "contain",
                          backgroundColor: "#f8f9fa",
                          padding: "10px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="btn btn-danger btn-sm position-absolute"
                        style={{ top: "35px", right: "10px" }}
                      >
                        <i className="fa-solid fa-times"></i>
                      </button>
                    </div>
                  )}
                </div>

                {/* Information Note */}
                <div className="col-md-12 mt-4">
                  <div className="alert alert-info">
                    <h6 className="alert-heading">
                      <i className="fa-solid fa-info-circle me-2"></i>
                      Important Note
                    </h6>
                    <p className="mb-0">
                      Only one app download poster can be created. Once created,
                      you can edit or delete it from the manage page. After
                      creating the poster, you will be redirected to the manage
                      page.
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="row mt-4">
                <div className="col-12">
                  <button
                    type="submit"
                    className="btn mt-3"
                    style={{
                      background: "rgb(133, 218, 79)",
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    disabled={loading || imageLoading}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin me-2"></i>
                        Adding Poster...
                      </>
                    ) : (
                      "Add App Poster"
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary mt-3 ml-2"
                    style={{
                      borderRadius: "13px",
                      padding: "10px 30px",
                      fontSize: "16px",
                      fontWeight: "500",
                    }}
                    onClick={clearForm}
                    disabled={!image}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AddAppPoster;
